import { pickOwnerUnionAddress } from "@/app/nft/search/filters/resolve-owner-filter"
import {
  type UnionAddress,
  getBlockchainFromAddress,
} from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"
import { BalanceMoleculeReatom } from "../profiles/balance/balance-molecule.reatom"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "../sdk/blockchains/blockchain-balance-sdk-provider.reatom"
import { WalletConnectMoleculeReatom } from "./store.reatom"

export const useFormattedAddress = (address: string) =>
  address.length > 8 ? `${address.slice(0, 4)}...${address.slice(-4)}` : address

export const useWalletConnect = () => {
  const {
    status,
    chain,
    unionAddress,
    primaryUnionAddress,
    address,
    evmAddress,
    solanaAddress,
    hasEvmWallet,
    hasSolanaWallet,
    evmUnionAddress,
    solanaUnionAddress,
    isConnected: isConnectedAtom,
    initialized,
    connectedBlockchain,
  } = useMolecule(WalletConnectMoleculeReatom)

  const statusValue = status()
  const addressValue = address()
  const evmAddressValue = evmAddress()
  const solanaAddressValue = solanaAddress()
  const baseBlockchain = chain()
  const unionAddressValue = unionAddress()
  const primaryUnionAddressValue = primaryUnionAddress()
  const evmUnionAddressValue = evmUnionAddress()
  const solanaUnionAddressValue = solanaUnionAddress()
  const hasEvmWalletValue = hasEvmWallet()
  const hasSolanaWalletValue = hasSolanaWallet()
  const isConnected = isConnectedAtom()
  const isWalletConnectorInitialized = initialized()
  const connectedBlockchainValue = connectedBlockchain().data

  return useMemo(
    () => ({
      address: addressValue,
      evmAddress: evmAddressValue,
      solanaAddress: solanaAddressValue,
      hasEvmWallet: hasEvmWalletValue,
      hasSolanaWallet: hasSolanaWalletValue,
      evmUnionAddress: evmUnionAddressValue,
      solanaUnionAddress: solanaUnionAddressValue,
      /** @deprecated Use evmUnionAddress / solanaUnionAddress. */
      primaryUnionAddress: primaryUnionAddressValue,
      status: statusValue,
      statusAtom: status,
      connectedBlockchain: connectedBlockchainValue,
      connectedBlockchainAtom: connectedBlockchain,
      isConnected,
      isWalletConnectorInitialized,
      /** @deprecated Use hasEvmWallet / hasSolanaWallet. */
      baseBlockchain,
      /** @deprecated Use primaryUnionAddress or evmUnionAddress / solanaUnionAddress. */
      unionAddress: unionAddressValue,
    }),
    [
      addressValue,
      evmAddressValue,
      solanaAddressValue,
      hasEvmWalletValue,
      hasSolanaWalletValue,
      evmUnionAddressValue,
      solanaUnionAddressValue,
      primaryUnionAddressValue,
      statusValue,
      status,
      connectedBlockchain,
      connectedBlockchainValue,
      isConnected,
      isWalletConnectorInitialized,
      baseBlockchain,
      unionAddressValue,
    ],
  )
}

export const useUserBalance = (): string | undefined => {
  const { nativeBalanceQueryParamsAtom } = useMolecule(BalanceMoleculeReatom)
  const queryParams = nativeBalanceQueryParamsAtom()
  const { data } = useQuery(queryParams)

  return data
}

export const useUserBalanceFromSdk = (
  blockchain: Blockchain,
): string | undefined => {
  const { balanceSDKAtom } = useMolecule(
    BlockchainBalanceSdkProviderMoleculeReatom,
  )
  const { evmAddress: evmAddressAtom, solanaAddress: solanaAddressAtom } =
    useMolecule(WalletConnectMoleculeReatom)
  const isSolana = blockchain === Blockchain.SOLANA
  const evmAddress = evmAddressAtom()
  const solanaAddress = solanaAddressAtom()
  const address = isSolana ? solanaAddress : evmAddress
  const balanceSDK = balanceSDKAtom()

  const { data } = useQuery({
    queryKey: ["userBalanceFromSdk", blockchain, address],
    queryFn: async () => {
      if (!balanceSDK || !address) return undefined
      if (!isSolana && !address.startsWith("0x")) return undefined
      return balanceSDK.getNativeBalance({ blockchain, address })
    },
    enabled: !!balanceSDK && !!address,
  })

  return data
}

export function useWrappedBalance(): string | undefined {
  const { wrappedBalanceQueryParamsAtom } = useMolecule(BalanceMoleculeReatom)
  const queryParams = wrappedBalanceQueryParamsAtom()
  const { data } = useQuery(queryParams)

  return data
}

export function usePortfolioUnionAddress(): UnionAddress | undefined {
  const { evmUnionAddress, solanaUnionAddress } = useWalletConnect()
  return evmUnionAddress ?? solanaUnionAddress
}

/** Wallet address for the collection's chain (Solana vs EVM when both connected). */
export function useOwnerUnionAddressForCollection(
  collectionId: CollectionId,
): UnionAddress | undefined {
  const { evmUnionAddress, solanaUnionAddress } = useWalletConnect()

  return useMemo(
    () =>
      pickOwnerUnionAddress([getBlockchainFromAddress(collectionId)], {
        evmUnion: evmUnionAddress,
        solanaUnion: solanaUnionAddress,
      }),
    [collectionId, evmUnionAddress, solanaUnionAddress],
  )
}
